import { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import Welcome from './components/Welcome';
import Onboarding from './components/onboarding/Onboarding';
import Login from './components/Login';
import Signup from './components/Signup';
import VerifyEmail from './components/VerifyEmail';
import Offers from './components/Offers';
import CodeRedemption from './components/CodeRedemption';
import Billing from './components/Billing';
import SubscriptionDetails from './components/SubscriptionDetails';
import EmailActionHandler from './components/EmailActionHandler';
import ProtectedRoute from './components/ProtectedRoute';
import PageViewTracker from './components/PageViewTracker';
import { initializeRemoteConfig, getOnboardingEnabled } from './services/firebase';
import { initializeAnalytics } from './services/analytics';
import { initializeGTM } from './services/gtm';
import { initializeIntercom } from './services/intercom';
import { initializeBranch, loadDealFromUrl } from './services/branch';
import { setServicesInitialized } from './services/servicesInitialized';
import { useLocale } from './i18n';
import './App.css';

function App() {
  // Re-render the whole tree when the language changes (so all t() re-evaluate)
  useLocale();
  // Read cached/default flag synchronously, refresh once Remote Config activates
  const [onboardingEnabled, setOnboardingEnabled] = useState<boolean>(getOnboardingEnabled());

  // Fast-path: if the URL carries ?deal=<id>, fetch + warm it immediately,
  // in parallel with (and without waiting for) the service init below.
  useEffect(() => {
    loadDealFromUrl();
  }, []);

  // Initialize Firebase Remote Config first, then other services
  useEffect(() => {
    const initializeServices = async () => {
      // Initialize Remote Config first (needed by all services)
      await initializeRemoteConfig();
      setOnboardingEnabled(getOnboardingEnabled());

      // Then initialize other services that depend on Remote Config
      await Promise.all([
        initializeAnalytics(),
        initializeGTM(),
        initializeIntercom(),
        initializeBranch(),
      ]);

      // Mark services as initialized
      setServicesInitialized();
      console.log('[App] ✅ All services initialized');
    };

    initializeServices();
  }, []);

  return (
    <Router>
      <PageViewTracker />
      <AuthProvider>
        <div className="App">
          <Routes>
            {/* Preserve the query string (Branch _branch_match_id, utm, etc.) across the
                initial redirect so Branch can attribute the deep-link click. */}
            <Route path="/" element={onboardingEnabled ? <Welcome /> : <Navigate to={`/signup${window.location.search}`} replace />} />
            <Route path="/onboarding" element={onboardingEnabled ? <Onboarding /> : <Navigate to={`/signup${window.location.search}`} replace />} />
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/auth/action" element={<EmailActionHandler />} />
            <Route
              path="/verify-email"
              element={
                <ProtectedRoute requireVerified={false}>
                  <VerifyEmail />
                </ProtectedRoute>
              }
            />
            <Route
              path="/offers"
              element={
                <ProtectedRoute>
                  <Offers />
                </ProtectedRoute>
              }
            />
            <Route
              path="/redeem"
              element={
                <ProtectedRoute>
                  <CodeRedemption />
                </ProtectedRoute>
              }
            />
            <Route
              path="/billing"
              element={
                <ProtectedRoute>
                  <Billing />
                </ProtectedRoute>
              }
            />
            <Route
              path="/subscription"
              element={
                <ProtectedRoute>
                  <SubscriptionDetails />
                </ProtectedRoute>
              }
            />
            <Route path="*" element={<Navigate to={`/${window.location.search}`} replace />} />
          </Routes>
        </div>
      </AuthProvider>
    </Router>
  );
}

export default App;
